import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Input,
  InputGroup, InputLeftElement, Icon, Select, HStack, Spinner, Flex, VStack,
} from '@chakra-ui/react';
import { FaSearch, FaClipboardList } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';

export default function FacilitySubmissions() {
  const { facilityId } = useOutletContext();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchSubmissions(); }, [facilityId]); // eslint-disable-line

  const fetchSubmissions = async () => {
    try {
      const { data } = await selectAll(() => supabase
        .from('submissions')
        .select('id, patient_name, patient_email, status, submitted_at, phlebotomist_name, doctor_name, blood_collection_time, draw_code')
        .eq('facility_id', facilityId)
        .order('submitted_at', { ascending: false }));
      setSubmissions(data || []);
    } catch (err) {
      console.error('Fetch submissions error:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (s) => {
    if (s === 'completed') return 'green';
    if (s === 'in_progress') return 'blue';
    if (s?.includes('waiting')) return 'orange';
    return 'purple';
  };

  const filtered = submissions.filter(s => {
    const matchSearch = !search ||
      (s.patient_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.doctor_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.draw_code || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <Box>
      <Heading size="lg" fontWeight="700" mb={1}>Submissions</Heading>
      <Text color="gray.500" fontSize="sm" mb={6}>Track all blood draw submissions from your facility</Text>

      <HStack spacing={4} mb={4}>
        <InputGroup maxW="350px">
          <InputLeftElement><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
          <Input placeholder="Search patient, doctor, draw code..." value={search} onChange={(e) => setSearch(e.target.value)} bg="white" />
        </InputGroup>
        <Select maxW="200px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} bg="white">
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </Select>
      </HStack>

      {loading ? (
        <Flex py={10} justify="center"><Spinner size="lg" /></Flex>
      ) : filtered.length === 0 ? (
        <VStack py={10} spacing={3}>
          <Icon as={FaClipboardList} boxSize={12} color="gray.300" />
          <Text color="gray.500">{search || statusFilter !== 'all' ? 'No submissions match your filters' : 'No submissions yet'}</Text>
        </VStack>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" shadow="sm">
          <Table size="sm">
            <Thead>
              <Tr bg="gray.50">
                <Th py={3}>Patient</Th>
                <Th py={3}>Draw Code</Th>
                <Th py={3}>Doctor</Th>
                <Th py={3}>Status</Th>
                <Th py={3}>Phlebotomist</Th>
                <Th py={3}>Submitted</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map(sub => (
                <Tr key={sub.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="500" fontSize="sm">{sub.patient_name || '—'}</Td>
                  <Td><Badge variant="outline" fontFamily="mono" fontSize="xs">{sub.draw_code || '—'}</Badge></Td>
                  <Td fontSize="sm" color="gray.600">{sub.doctor_name || '—'}</Td>
                  <Td><Badge colorScheme={statusColor(sub.status)} fontSize="xs">{(sub.status || 'pending').replace(/_/g, ' ').toUpperCase()}</Badge></Td>
                  <Td fontSize="sm" color="gray.600">{sub.phlebotomist_name || 'Not assigned'}</Td>
                  <Td fontSize="sm" color="gray.500">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '—'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
